import { IInterestManagementData } from '@/app/interest-management/entities/interest-management-data.interface';
import { IInterestManagementState } from '@/app/interest-management/entities/interest-management-state.interface';
import { IInterestTypeSelection } from '@/app/interest-management/entities/interest-type-selection.interface';
import { INoticeState } from '@/app/interest-management/entities/notice-state.interface';
import { InterestManagementState } from '@/app/interest-management/interest-management.state';
import { ConfigurationProvider } from '@/app/interest-management/providers/configuration.provider';
import { IInterestState } from '@/app/interest-management/providers/interest-state.interface';
import { IFormGroupState } from '@/components/atoms/form/form-group/form-group-state.interface';
import { ISwitchState } from '@/components/atoms/form/switch/switch-state.interface';
import { IIconNoticeState } from '@/components/molecules/notices/icon-notice/icon-notice-state.interface';
import { NoticeType } from '@/components/molecules/notices/notice-type.definition';
import {
    AnalyticsEventName,
    GTMUtils,
    InterestSession,
    SessionStore,
    SessionStoreKey,
    StorageService,
    SubscriptionResource,
    TranslationKey,
    TranslationService
} from '../../../../../modules';
import { InterestEntity } from '../../../../../modules/resource/interest/interest-entity.interface';
import { InterestResource } from '../../../../../modules/resource/interest/interest.resource';
import { Interest } from "@/app/interest-management/entities/interest.enum";

export class InterestManagementController {

    private readonly applicationData: IInterestManagementData;

    private interestRequestCache: Map<string, IInterestState[]> = new Map([]);

    private _state: InterestManagementState;

    private _translations: {
        artistSeparatorLabel: string;
        interestSeparatorLabel: string;
        interestTypeSelectionLabel: string;
        limitByName: string;
        searchByName: string;
    } | null = null;

    constructor(
        private configurationProvider: ConfigurationProvider,
        private interestResource: InterestResource,
        private subscriptionResource: SubscriptionResource,
        private translationService: TranslationService,
        private storageService: StorageService,
    ) {
        this.applicationData = this.configurationProvider.getApplicationData();

        this._state = new InterestManagementState(this.applicationData);
        this._state.decorateInterestsWithTranslation(translationService).catch(e => console.error(e));
        this._state.updateAutomaticSubscriptionWith(
            this.determineAutomaticSubscriptionFor(this.applicationData, this.storageService.loadSession())
        );

        this.initTranslations().catch(e => console.error(e));

        if (this._state.state.automaticSubscription) {
            this.createSubscription().catch();
        }
    }

    get translations(): any {
        return this._translations;
    }

    get state(): IInterestManagementState {
        return this._state.state;
    }

    async initTranslations(): Promise<any> {
        const tCollection = await this.translationService.getTranslations();

        this._translations = {
            artistSeparatorLabel: tCollection[TranslationKey.Search_artists],
            interestSeparatorLabel: tCollection[TranslationKey.Search_interests],
            interestTypeSelectionLabel: tCollection[TranslationKey.Interest_subscription_select_type],
            limitByName: tCollection[TranslationKey.Interest_subscription_limit_by_name],
            searchByName: tCollection[TranslationKey.Search_by_name],
        };
    }

    async handleInterestTypeChange(selection: IInterestTypeSelection): Promise<void> {
        this._state.updateInterestTypeWith(selection);
        await this.handleInterestQueryChange();
    }

    async createSubscription(): Promise<boolean> {
        if (!this.hasValidSubscription(this.state)) {
            return false;
        }

        try {
            const payload = this._state.getSubscriptionPayload();

            await this.subscriptionResource.createSubscriptionWith(payload);
            let noticeState: INoticeState | null = this._state.clone.notice;
            let translation: string | null;

            if (this._state.state.automaticSubscription) {
                const titleTranslation = await this.translationService.getTranslationFor(TranslationKey.Interest_subscription_general_success_title);
                const session = this.storageService.loadSession();
                let automaticSubscriptionNoticeState: INoticeState | null = this._state.clone.automaticSubscriptionNotice;

                translation = await this.translationService.getTranslationFor(TranslationKey.Interest_subscription_success_description);
                translation = TranslationService.decorateParams(translation, {
                    key: TranslationKey.Interest_subscription_success_description,
                    keyValues:  JSON.stringify({ 'userEmail': this.state.email.value }),
                })

                this._state.updateAutomaticSubscriptionNotice({
                    ...automaticSubscriptionNoticeState,
                    type: NoticeType.Success,
                    title: titleTranslation,
                    content: translation,
                    state: true
                });
                this._state.updateAutomaticSubscriptionWith(false);

                if (!session[SessionStoreKey.Interest]) {
                    session[SessionStoreKey.Interest] = { autoSubscriptionRequest: [] };
                }
                session[SessionStoreKey.Interest]?.autoSubscriptionRequest.push(payload.userEmail);
                this.storageService.saveSession(session);

                automaticSubscriptionNoticeState = null;
                translation = null;
                return true;
            }

            translation = await this.translationService.getTranslationFor(TranslationKey.Interest_subscription_email_sent);
            translation = TranslationService.decorateParams(translation, {
                key: TranslationKey.Interest_subscription_email_sent,
                keyValues:  JSON.stringify({ 'email': this.state.email.value }),
            })

            this._state.updateNotice({ ...noticeState, type: NoticeType.Success, content: translation, state: true });
            noticeState = null;
            translation = null;

            GTMUtils.pushAnalyticsEventToDataLayer({
                event: AnalyticsEventName.ManageInterestsSave,
                weekly_guide: this.getAnalyticsValueFor(Interest.General),
                special_offers: this.getAnalyticsValueFor(Interest.Offers),
                marketing_consent: this.getAnalyticsValueFor(Interest.MarketingConsent),
            });

            return true;
        } catch (e) {
            let noticeState: INoticeState | null = this._state.clone.notice;
            let translation: string | null = await this.translationService.getTranslationFor(TranslationKey.Interest_subscription_error);

            this._state.updateNotice({ ...noticeState, type: NoticeType.Error, content: translation, state: true });
            noticeState = null;
            translation = null;

            return false;
        }
    }

    async updateArtistAlarmQuery(data: IFormGroupState): Promise<void> {
        this._state.updateArtistQueryWith(data);
        await this.handleArtistAlarmQueryChange();
    }

    async updateInterestQuery(data: IFormGroupState): Promise<void> {
        this._state.updateInterestQueryWith(data);
        await this.handleInterestQueryChange();
    }

    handleNoContactChange(interest: IInterestState, state: IIconNoticeState): void {
        if (!state.switchState) {
            return;
        }

        interest.state = state.switchState.state;
        this._state.updateNoContactWith(interest);
    }

    hasValidSubscription(state: IInterestManagementState): boolean {
        if (!state.email.valid) {
            return false;
        }

        return !(state.interests.length === 0 && state.removedInterests.length === 0);
    }

    getDppNotice(): string {
        return this.applicationData.dppNotice;
    }

    getArtistAlarmResults(): IInterestState[] {
        const clone = this._state.clone;
        return clone.artistInterestResults
            .filter(item => !clone.artistInterests.some(i => i.type === item.type && i.resourceId === item.resourceId))
            .filter((item) => !item.state)
            .filter((item, index) => index < 10);
    }

    getInterestResults(): IInterestState[] {
        const clone = this._state.clone;
        return clone.topicInterestResults
            .filter(item => !clone.topicInterests.some(i => i.type === item.type && i.resourceId === item.resourceId))
            .filter((item) => !item.state)
            .filter((item, index) => index < 10);
    }

    updateStateEmail(data: IFormGroupState): void {
        this._state.updateEmail(data);
    }

    handleInterestChange(interest: IInterestState, switchState: ISwitchState): void {
        this._state.updateStateFor({ ...interest, state: switchState.state });
    }

    handleAutomaticSubscriptionNoticeInteraction(): void {
        let currentState: INoticeState | null = this._state.clone.automaticSubscriptionNotice;

        this._state.updateAutomaticSubscriptionNotice({ ...currentState, state: !currentState.state });
        currentState = null;
    }

    handleNoticeInteraction(): void {
        let currentState: INoticeState | null = this._state.clone.notice;

        this._state.updateNotice({ ...currentState, state: !currentState.state });
        currentState = null;
    }

    private async handleArtistAlarmQueryChange(): Promise<void> {
        const query = this.state.artistInterestQuery;
        const requestId = JSON.stringify('artist_alarm_' + query.name.value);

        this._state.setProgress(true, 'interest');

        if (query.name.value.length < 2) {
            this._state.updateArtistResultsWith([]);
            return;
        }

        if (this.interestRequestCache.has(requestId)) {
            const requestedInterest = this.interestRequestCache.get(requestId);

            if (requestedInterest) {
                this._state.updateArtistResultsWith(requestedInterest);
            }

            return;
        }

        try {
            const response = (await this.interestResource.getTicketAlarmInterestsFor(query.name.value))
                .map(entity => this.mapToInterestStateFrom(entity));

            this.cacheResponse(requestId, response);
            this._state.updateArtistResultsWith(response);

            return undefined;
        } catch (e) {
            this._state.setProgress(false, 'interest');
            return undefined;
        }
    }

    private async handleInterestQueryChange(): Promise<void> {
        const query = this.state.topicInterestQuery;

        if (!query.type || !query.type.value) {
            this._state.updateInterestResultsWith([]);
            return;
        }

        if (!query.name.valid || query.name.value.length === 0) {
            return;
        }

        const requestId = JSON.stringify(query.type.value + '_' + query.name.value);

        if (query.type.value === 'venue' &&  query.name.value.length < 3) {
            this._state.updateInterestResultsWith([]);
            return;
        }

        this._state.setProgress(true, 'interest');
        if (this.interestRequestCache.has(requestId)) {
            const requestedInterest = this.interestRequestCache.get(requestId);

            if (requestedInterest) {
                this._state.updateInterestResultsWith(requestedInterest);
            }
            
            return;
        }

        try {
            const response = (query.type.value === 'category'
                ? await this.interestResource.getCategoryInterestsFor(query.name.value)
                : await this.interestResource.getVenueInterestsFor(query.name.value)
            ).map(entity => this.mapToInterestStateFrom(entity));

            this.cacheResponse(requestId, response);
            this._state.updateInterestResultsWith(response);

            return undefined;
        } catch (e) {
            this._state.setProgress(false, 'interest');
            return undefined;
        }
    }

    private getAnalyticsValueFor(interestName: string): string {
        let interest = this.state.eventimInterests.find(interest => interest.resourceName === interestName);

        if (interestName === Interest.MarketingConsent) {
            interest = this.state.noContactInterest;
        }

        return interest && interest.state ? 'on' : 'off';
    }

    private cacheResponse(id: string, response: IInterestState[]): void {
        this.interestRequestCache.set(id, response);
    }

    private determineAutomaticSubscriptionFor(applicationData: IInterestManagementData, session: SessionStore): boolean {
        const interestSession: InterestSession | undefined = session[SessionStoreKey.Interest];

        if (applicationData.user || !applicationData.userEmail) {
            return false;
        }

        if (!interestSession) {
            return true;
        }

        return Object.values(interestSession.autoSubscriptionRequest).indexOf(applicationData.userEmail) === -1;
    }

    private mapToInterestStateFrom(entity: InterestEntity): IInterestState {
        return  {
            ...entity,
            state: false,
            initialState: false,
            title: entity.resourceName,
        };
    }

}
